import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dices, Repeat, X } from 'lucide-react';
import type { DrawnOpeningInfo } from '@draughts/shared';
import { titoloApertura } from '../../utils/opening-labels';
import './DrawnOpeningBanner.css';

interface DrawnOpeningBannerProps {
  /**
   * The drawn opening for the current game (null for free openings). Its `id` is the
   * federal table number, and it is there only when the draw really came from a table
   * — see `ballotNumber`.
   */
  opening: DrawnOpeningInfo | null;
  /** Changes on each new game/rematch start; re-arms the transient banner. */
  triggerKey: number | null;
  /** "Partita 1 di 2" chip for the legs of a two-leg match (andata e ritorno). */
  legLabel?: string;
}

/** Nothing to announce: no drawn opening AND no match to place the game in. */
const nothingToSay = (opening: unknown, legLabel?: string): boolean => !opening && !legLabel;

/* Dieci secondi: a cinque l'apertura spariva prima che si finisse di leggerla, e la
   pastiglia si lascia attraversare dai tocchi, quindi restare non costa nulla. */
const AUTO_DISMISS_MS = 10000;

/**
 * Transient start-of-game banner: it slides in over the top of the board, then
 * auto-dismisses (also closable). It says whichever of these two is true —
 *
 *  - the game started from a DRAWN opening, showing the moves it drew. The board
 *    already shows the post-opening position; this is purely the cue explaining
 *    what happened;
 *  - the game is one leg of a match ("Partita 1 di 2").
 *
 * The second one used to ride inside the first, which was fine while only a drawn
 * opening could be played andata e ritorno: now a free opening can too, and a
 * player would have had nothing at all telling them they are in a match.
 */
export default function DrawnOpeningBanner({ opening, triggerKey, legLabel }: DrawnOpeningBannerProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  // Re-arm on a new game (triggerKey) AND on a new opening object. Keying on the
  // opening too matters for the return leg of a two-leg match: there the banner
  // is already mounted and the opening can land in a different render than the
  // key change, which a triggerKey-only effect would miss (banner never shown).
  useEffect(() => {
    if (nothingToSay(opening, legLabel)) {
      setVisible(false);
      return;
    }
    setVisible(true);
    const timer = window.setTimeout(() => setVisible(false), AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [triggerKey, opening, legLabel]);

  if (!visible || nothingToSay(opening, legLabel)) return null;

  const movesText = !opening
    ? ''
    : opening.moves && opening.moves.length > 0
      ? opening.moves.join('  ')
      : (opening.displayText ?? '');

  // Numero di tabella e forza: li compone `titoloApertura`, che e' lo stesso
  // titolo della lista mosse e del cartellino pre-partita.
  const titolo = titoloApertura(t, opening);

  return (
    /* `dob-apertura` porta le misure PIÙ GRANDI: la classe base è condivisa con
       l'avviso d'errore e il rifai-mossa, che in `.dob-label` ci mettono frasi intere
       e a 360px andrebbero a capo su tre righe. */
    <div className="drawn-opening-banner dob-apertura" role="status">
      {/* Senza pescata l'icona non è più il dado ma il ritorno: è l'unica cosa
          che questo banner sta annunciando. */}
      <span className="dob-icon" aria-hidden="true">{opening ? <Dices size={20} /> : <Repeat size={20} />}</span>
      <span className="dob-text">
        <span className="dob-label">
          {/* Con la pescata la manche è una pastiglia accanto al titolo; senza,
              è il titolo — quindi niente pastiglia (e niente suo margine). */}
          {titolo ? (
            <>
              {/* Il numero di tabella E la forza: è la coppia che l'arbitro annuncia,
                  ed è quella che identifica la riga del Regolamento. Senza una tabella
                  dietro (pescata casuale, libro International dei bot) non c'è nessuno
                  dei due e resta il titolo semplice: un numero inventato sarebbe
                  peggio di nessuno. */}
              {titolo.titolo}
              {titolo.codice && <span className="dob-forza"> ({titolo.codice})</span>}
              {legLabel && <span className="dob-leg">{legLabel}</span>}
            </>
          ) : legLabel}
        </span>
        {movesText && <span className="dob-moves">{movesText}</span>}
      </span>
      <button
        type="button"
        className="dob-close"
        aria-label={t('common.close', 'Close')}
        onClick={() => setVisible(false)}
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}
